import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { CARRIERS, CLASSES, listCarrierRates, MODES, rowError, setCarrierRate } from '../../lib/marketAdminCarrierRates';

// Shipping rates for the products the partner refuses (owner, 2026-11-02 — point 2). One row per
// carrier × mode × class; the cart takes the cheaper carrier for each cart (its minimum counted).
// An empty rate removes the row: that option then shows «اطلب سعر الشحن» for those products.
// Every save is audited (audit_log 'market_carrier_rate_set').

const T = {
  ar: {
    title: 'أسعار شحن المنتجات المرفوضة', sub: 'المنتجات اللي يرفضها شريك الشحن (مستحضرات، سوائل، كاميرات …) تُشحن بحرًا وجوًا بأرخص شركة من الاثنتين لكل سلة. السعر من الباب للباب شامل الشحن والجمارك بالريال. بدون سعر: الخيار يظهر للتاجر «اطلب سعر الشحن». التاجر لا يرى اسم الشركة.',
    carriers: { hiobuy: 'HioBuy', dhl_gf: 'DHL Global Forwarding' },
    modes: { air: 'جوي — ر.س لكل كجم محسوب', sea: 'بحري — ر.س لكل م³' },
    minUnit: { air: 'كجم', sea: 'م³' },
    classes: { general: 'عام', electrical: 'كهربائي', sensitive: 'حساس (بطاريات)' },
    rate: 'السعر', min: 'الحد الأدنى للسلة', days: 'أيام التوصيل', on: 'مفعّل', note: 'ملاحظة (مصدر السعر)', save: 'حفظ', clear: 'حذف',
    saved: 'تم الحفظ', removed: 'تم الحذف — الخيار يرجع «اطلب سعر الشحن»',
    errs: { rate: 'السعر لازم يكون أكبر من صفر', min: 'الحد الأدنى رقم غير صحيح', days: 'الأيام أرقام صحيحة، والأقل قبل الأكثر' },
    empty: 'لا يوجد سعر — «اطلب سعر الشحن»',
  },
  en: {
    title: 'Refused products — shipping rates', sub: 'Products the partner refuses (cosmetics, liquids, cameras …) go by sea and air with the cheaper of the two carriers for each cart. Door to door, shipping and customs included, SAR. No rate: the trader sees «Request a shipping price». Traders never see the carrier.',
    carriers: { hiobuy: 'HioBuy', dhl_gf: 'DHL Global Forwarding' },
    modes: { air: 'Air — SAR per chargeable kg', sea: 'Sea — SAR per m³' },
    minUnit: { air: 'kg', sea: 'm³' },
    classes: { general: 'General', electrical: 'Electrical', sensitive: 'Sensitive (batteries)' },
    rate: 'Rate', min: 'Cart minimum', days: 'Transit days', on: 'On', note: 'Note (rate source)', save: 'Save', clear: 'Remove',
    saved: 'Saved', removed: 'Removed — that option asks for a price again',
    errs: { rate: 'The rate must be above zero', min: 'The minimum is not a number', days: 'Days are whole numbers, the lower first' },
    empty: 'No rate — «Request a shipping price»',
  },
};

const key = (carrier, mode, cls) => `${carrier}|${mode}|${cls}`;

export default function ConsoleMarketCarrierRates({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const [draft, setDraft] = useState(null);
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2800); };

  const load = useCallback(async () => {
    const r = await listCarrierRates();
    if (!r.ok) { flash(r.error); setDraft({}); return; }
    const d = {};
    for (const x of r.data) d[key(x.carrier, x.mode, x.air_class)] = { rate: String(x.rate_sar), min: String(x.min_qty ?? ''), days_min: String(x.days_min), days_max: String(x.days_max), enabled: x.enabled, note: x.note || '', saved: true };
    setDraft(d);
  }, []);
  useEffect(() => { load(); }, [load]);

  const edit = (k, patch) => setDraft({ ...draft, [k]: { ...(draft[k] || { enabled: true }), ...patch, saved: false } });
  const save = async (carrier, mode, cls, clear = false) => {
    const k = key(carrier, mode, cls);
    const row = clear ? { rate: '' } : (draft[k] || {});
    const e = rowError(row);
    if (e) { flash(c.errs[e]); return; }
    const r = await setCarrierRate(carrier, mode, cls, row);
    if (!r.ok) { flash(r.error); return; }
    flash(clear || !String(row.rate || '').trim() ? c.removed : c.saved);
    load();
  };

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-carrier-rates">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head"><div><h1 className="ac-h1">{c.title}</h1><p className="ac-sub">{c.sub}</p></div></div>
        {!draft ? <p className="ac-sub">…</p> : MODES.map((mode) => (
          <div key={mode} className="ac-card" style={{ marginBottom: 14 }} data-rates-mode={mode}>
            <h2 style={{ fontSize: 15, margin: "0 0 10px" }}>{c.modes[mode]}</h2>
            <table className="ac-table">
              <thead><tr><th /><th>{c.rate}</th><th>{c.min} ({c.minUnit[mode]})</th><th>{c.days}</th><th>{c.on}</th><th>{c.note}</th><th /></tr></thead>
              <tbody>
                {CARRIERS.flatMap((carrier) => CLASSES.map((cls) => {
                  const k = key(carrier, mode, cls);
                  const r = draft[k] || { enabled: true };
                  const has = !!String(r.rate || '').trim();
                  return (
                    <tr key={k} data-rate-row={k} data-rate-saved={r.saved ? '1' : '0'}>
                      <td><strong>{c.carriers[carrier]}</strong> · {c.classes[cls]}{!has && <div className="ac-sub" style={{ margin: 0 }}>{c.empty}</div>}</td>
                      <td><input className="ac-input" dir="ltr" style={{ width: 80 }} value={r.rate || ''} onChange={(e) => edit(k, { rate: e.target.value })} /></td>
                      <td><input className="ac-input" dir="ltr" style={{ width: 70 }} value={r.min || ''} onChange={(e) => edit(k, { min: e.target.value })} /></td>
                      <td dir="ltr" style={{ whiteSpace: 'nowrap' }}>
                        <input className="ac-input" style={{ width: 46 }} value={r.days_min || ''} onChange={(e) => edit(k, { days_min: e.target.value })} />
                        {' – '}
                        <input className="ac-input" style={{ width: 46 }} value={r.days_max || ''} onChange={(e) => edit(k, { days_max: e.target.value })} />
                      </td>
                      <td><input type="checkbox" checked={r.enabled !== false} onChange={(e) => edit(k, { enabled: e.target.checked })} /></td>
                      <td><input className="ac-input" style={{ width: 170 }} value={r.note || ''} onChange={(e) => edit(k, { note: e.target.value })} /></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button type="button" className="ac-btn ac-btn-sm" data-rate-save={k} onClick={() => save(carrier, mode, cls)}>{c.save}</button>
                        {r.saved && has && <button type="button" className="ac-btn ac-btn-sm" style={{ marginInlineStart: 6 }} onClick={() => save(carrier, mode, cls, true)}>{c.clear}</button>}
                      </td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          </div>
        ))}
        {toast && <div className="ac-toast" role="status">{toast}</div>}
      </div>
    </ConsoleShell>
  );
}
